import { useState } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { interviewsApi, applicationsApi } from '@/lib/api'
import { PageLayout } from '@/components/layout/PageLayout'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toaster'

export function NewInterviewPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)

  const [title, setTitle] = useState('')
  const [applicationId, setApplicationId] = useState('')
  const [type, setType] = useState('video')
  const [scheduledDate, setScheduledDate] = useState('')
  const [durationMinutes, setDurationMinutes] = useState('60')
  const [location, setLocation] = useState('')
  const [videoLink, setVideoLink] = useState('')
  const [notes, setNotes] = useState('')

  const { data: appsData } = useQuery({
    queryKey: ['applications'],
    queryFn: () => applicationsApi.list(),
  })
  const applications = (appsData as any[]) || []

  const createInterview = useMutation({
    mutationFn: (data: unknown) => interviewsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] })
      toast({ title: 'Interview scheduled', variant: 'success' })
      navigate({ to: '/interviews' })
    },
    onError: (err) => {
      toast({
        title: 'Failed to schedule interview',
        description: err instanceof Error ? err.message : 'Something went wrong',
        variant: 'destructive',
      })
    },
  })

  const canSubmit = title && applicationId && scheduledDate

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    const scheduledAt = Math.floor(new Date(scheduledDate).getTime() / 1000)
    createInterview.mutate({
      applicationId,
      title,
      type,
      scheduledAt,
      durationMinutes: parseInt(durationMinutes) || 60,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      location: location || undefined,
      videoLink: videoLink || undefined,
      notes: notes || undefined,
      participantIds: user ? [user.id] : [],
    })
  }

  return (
    <PageLayout fullHeight back="/interviews" title="Schedule Interview" maxWidth="2xl">
      <Card className="h-full flex flex-col">
        <CardContent className="flex-1 min-h-0 flex flex-col pt-6">
          <form onSubmit={handleSubmit} className="h-full flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="int-title">Title *</Label>
                  <Input id="int-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Technical Interview - Round 1" required />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="int-application">Application *</Label>
                  <Select value={applicationId} onValueChange={setApplicationId}>
                    <SelectTrigger id="int-application">
                      <SelectValue placeholder="Select application..." />
                    </SelectTrigger>
                    <SelectContent>
                      {applications.map((app: any) => (
                        <SelectItem key={app.id} value={app.id}>
                          {app.candidate?.firstName} {app.candidate?.lastName} — {app.job?.title || 'Unknown'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="int-type">Type</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger id="int-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="onsite">On-site</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="behavioral">Behavioral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="int-duration">Duration (min)</Label>
                  <Input id="int-duration" type="number" min={15} max={480} value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="int-date">Date & Time *</Label>
                  <Input id="int-date" type="datetime-local" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="int-location">Location</Label>
                  <Input id="int-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Room 302" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="int-video">Video Link</Label>
                  <Input id="int-video" value={videoLink} onChange={(e) => setVideoLink(e.target.value)} placeholder="https://zoom.us/j/..." />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="int-notes">Notes</Label>
                  <Textarea id="int-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes for interviewers..." rows={3} />
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 flex justify-end gap-2 pt-3 border-t mt-3">
              <Button type="button" variant="outline" asChild>
                <Link to="/interviews">Cancel</Link>
              </Button>
              <Button type="submit" disabled={!canSubmit || createInterview.isPending}>
                {createInterview.isPending ? 'Scheduling...' : 'Schedule Interview'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </PageLayout>
  )
}
